const {
  TP,
  OPERATORS,
  ENTITY_TYPES,
  FILTER_LABELS,
  FILTER_TYPES,
  CASTING_RESULT_TYPES_VALUES,
} = require('../constants');

const BOOLEAN_OPERATORS = {
  AND: ';',
  OR: '|',
};

const BOOLEAN_OPERATOR_MAP = {
  [BOOLEAN_OPERATORS.AND]: OPERATORS.AND,
  [BOOLEAN_OPERATORS.OR]: OPERATORS.OR,
};

const BOOLEAN_OPERATOR_REGEX = new RegExp(`([${Object.values(BOOLEAN_OPERATORS).join('')}])`, 'g');

const cleanedExpression = expression => {
  if (!expression || typeof expression !== 'string') {
    return '';
  }

  const withRestMatch = expression.match(/^!(w\d+:(q|r)\d+);?\((.*)\)$/);
  if (withRestMatch) {
    const firstPart = withRestMatch[1];
    const rest = withRestMatch[3];
    return `${firstPart};${rest}`;
  }

  const singlePartMatch = expression.match(/^!(w\d+:(q|r)\d+);?$/);
  if (singlePartMatch) {
    return `${singlePartMatch[1]};`;
  }

  return expression;
};

const getBooleanQueryParts = expression => {
  const parts = expression
    .replace(BOOLEAN_OPERATOR_REGEX, '*$1*')
    .split('*')
    .filter(Boolean);

  const booleanQuery = [];
  let currentGroup = { slugs: [] };

  parts.forEach(part => {
    if (part in BOOLEAN_OPERATOR_MAP) {
      if (currentGroup.slugs.length > 0) {
        booleanQuery.push({ ...currentGroup });
      }
      currentGroup = { slugs: [], operator: BOOLEAN_OPERATOR_MAP[part] };
    } else {
      const [wSlug, rSlug] = part.split(':');
      currentGroup.slugs.push(rSlug, wSlug);
    }
  });

  if (currentGroup.slugs.length > 0) {
    booleanQuery.push(currentGroup);
  }

  return booleanQuery;
};

const getHeadlineParts = ({ entityType, attributes = [], translationFn }) => {
  if ([ENTITY_TYPES.PROFESSION, ENTITY_TYPES.WORK_ROLE, ENTITY_TYPES.ENSEMBLE_TYPE].includes(entityType)) {
    return {
      headline: [translationFn(`${TP}.FN_ROLE_TITLE`)],
    };
  }

  if (entityType === ENTITY_TYPES.WORK && attributes.length > 0) {
    const workType = attributes.find(attr => attr.entityType === ENTITY_TYPES.WORK_TYPE)?.name;
    const composer = attributes.find(attr => attr.entityType === ENTITY_TYPES.PROFILE);
    const headline = [];

    if (workType) {
      headline.push(workType);
    }

    return {
      headline: [...headline, translationFn(`${TP}.m_MUSICALWORK`)],
      ...(composer && {
        composers: [
          {
            ...composer,
            identifier: {
              entityType: composer.entityType,
              entityId: composer.id,
            },
          },
        ],
      }),
    };
  }

  return {
    headline: [translationFn(`${TP}.m_MUSICALWORK`)],
  };
};

const getCountryH1Block = ({ country = [], translationFn }) => {
  const joinedNames = country
    .reduce((acc, { entity }) => {
      const name = entity?.name;
      if (name) acc.push(name);
      return acc;
    }, [])
    .join(', ');

  return [
    {
      name: joinedNames || translationFn(`${TP}.FN_ALL`),
      headline: [translationFn(`${TP}.m_COUNTRY`)],
    },
  ];
};

const getRoleDebutTitleParts = (translationFn, entity, index, subIndex, groupLength) => {
  if (index === 0) {
    return {
      subTitle: translationFn(`${TP}.ROLE_DEBUT_FOR`),
      operator: undefined,
    };
  }

  if (groupLength > 1 && index === 1 && subIndex === groupLength - 1) {
    return {
      subTitle: translationFn(`${TP}.SHOULD_HAVE_PERFORMED`),
      operator: undefined,
    };
  }

  return entity;
};

const getDesiredRoleTitleParts = (translationFn, entity, index, subIndex, groupLength) => {
  if (index === 0) {
    return {
      subTitle: translationFn(`${TP}.FN_WITH_DESIRED_ROLE`),
      operator: undefined,
    };
  }

  if (groupLength > 1 && index === 1 && subIndex === groupLength - 1) {
    return {
      subTitle: translationFn(`${TP}.SHOULD_HAVE_PERFORMED`),
      operator: undefined,
    };
  }

  return entity;
};

const getCastingToolH1Title = async ({ filters, translationFn, entityFinder, tab }) => {
  const { expression, since_year, country } = filters || {};
  const cleanExpression = cleanedExpression(expression);
  const booleanQueryParts = cleanExpression ? getBooleanQueryParts(cleanExpression) : [];

  const entityGroupsPromises = await Promise.all(
    booleanQueryParts.map(async group => {
      const { slugs } = group;
      const result = await entityFinder(slugs);

      const entityData = result?.data?.flatMap(
        item =>
          item?.entities?.map(entityDetails => {
            const { attributes, name, entityType } = entityDetails || {};
            const { headline, composers } = getHeadlineParts({
              entityType,
              attributes,
              translationFn,
            });

            return {
              name,
              ...(headline?.length > 0 && { headline }),
              ...(composers?.length > 0 && { composers }),
              ...([ENTITY_TYPES.PROFESSION, ENTITY_TYPES.WORK_ROLE, ENTITY_TYPES.ENSEMBLE_TYPE].includes(
                entityType,
              ) && { operator: group.operator }),
            };
          }) ?? [],
      );

      return entityData.map(entity => [entity]);
    }),
  );

  const entityGroups = entityGroupsPromises.flat().reduce((entities, entity) => {
    if (entity?.[0]?.operator) {
      entities[entities.length - 1].push(entity?.[0]);
    } else {
      entities.push(entity);
    }
    return entities;
  }, []);

  const sinceYearH1Part =
    since_year && since_year.length > 0
      ? [[{ name: since_year[0]?.id, headline: [translationFn(FILTER_LABELS[FILTER_TYPES.SINCE_YEAR])] }]]
      : [];
  const isDesiredRole = tab === CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE;
  const isRoleDebut = tab === CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT;
  const countryH1Part = isDesiredRole ? [] : [getCountryH1Block({ country, translationFn })];
  const updatedEntityGroups =
    isRoleDebut || isDesiredRole
      ? entityGroups.map((group, index) =>
          group.map((entity, subIndex) => ({
            ...entity,
            ...(isDesiredRole && getDesiredRoleTitleParts(translationFn, entity, index, subIndex, group?.length)),
            ...(isRoleDebut && getRoleDebutTitleParts(translationFn, entity, index, subIndex, group?.length)),
          })),
        )
      : entityGroups;

  return [...updatedEntityGroups, ...sinceYearH1Part, ...countryH1Part];
};

module.exports = getCastingToolH1Title;
